// Founding-member proof block — audit D3.1 / D3.2.
//
// The audit's recommended fix is to ship the first 5 named testimonials
// with photos, roles and specific outcome quotes, PLUS a small counter
// of founding members filled out of 100 in the same block. The founder
// brief confirms no customer quotes have been collected yet, so the
// grid shows an honest empty state until they arrive — the counter and
// invitation CTA always render.
//
// The counter is wired to the LIVE paid-signup count via
// GET /api/founding-members/count, which counts distinct non-test
// customers with at least one successful payment_transactions row
// (capped at 100). As paying founding members land, this ticker
// bumps itself.
//
// Do NOT fabricate testimonials. When real quotes arrive, replace the
// FOUNDING_MEMBERS placeholder array below and the empty-state will
// swap out automatically.
//
// Content required from founder (flagged in change log):
//   • 5 testimonials: firstName, roleOrCompany, outcomeQuote,
//     photoUrl (served from Emergent Object Storage), with explicit
//     permission.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users } from "lucide-react";

// Replace this array with real quotes once collected. Keep the shape:
//   { id, firstName, roleOrCompany, outcomeQuote, photoUrl }
const FOUNDING_MEMBERS = [];

const FOUNDING_MEMBER_CAP = 100;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function FoundingMembers() {
  const hasQuotes = FOUNDING_MEMBERS.length > 0;
  const [filled, setFilled] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/founding-members/count`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const n = Number(data?.filled);
        const cap = Number(data?.cap) || FOUNDING_MEMBER_CAP;
        if (Number.isFinite(n) && n >= 0) {
          setFilled(Math.min(n, cap));
        }
      } catch {
        // Silent fallback — the honest 0 default keeps the UI truthful.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const pct = Math.round((filled / FOUNDING_MEMBER_CAP) * 100);

  return (
    <section
      className="zy-section bg-white"
      aria-labelledby="founding-members-title"
      data-testid="founding-members"
      style={{ paddingTop: 72, paddingBottom: 72 }}
    >
      <div className="max-w-5xl mx-auto px-6">
        <div className="max-w-2xl">
          <p className="zy-eyebrow mb-3">Founding members</p>
          <h2 id="founding-members-title" className="zy-h2">
            Join the first {FOUNDING_MEMBER_CAP} founding members.
          </h2>
          <p className="zy-body mt-4">
            Zynthoro is in its founding period. The first {FOUNDING_MEMBER_CAP}{" "}
            paying customers can choose to be featured as founding members
            after giving explicit permission.
          </p>

          {/* Audit D3.1 — adoption number, stated honestly, auto-bumped
              from the live paid-signup count. */}
          <div
            className="mt-6 flex items-center gap-4"
            data-testid="founding-members-counter"
            aria-label={`${filled} of ${FOUNDING_MEMBER_CAP} founding members so far`}
          >
            <p className="text-[14px] font-semibold text-[#0A1628]">
              <span className="text-[#202B60]">{filled}</span>{" "}
              of {FOUNDING_MEMBER_CAP} founding members so far
            </p>
            <div
              className="hidden sm:block flex-1 max-w-[220px] h-1.5 rounded-full bg-[#eef1f7] overflow-hidden"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={FOUNDING_MEMBER_CAP}
              aria-valuenow={filled}
            >
              <div
                className="h-full bg-[#D4AF37] transition-[width] duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </div>

        {hasQuotes ? (
          <div
            className="mt-9 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid="founding-members-grid"
          >
            {FOUNDING_MEMBERS.map((m) => (
              <figure
                key={m.id}
                className="rounded-2xl border border-[#eef1f7] bg-[#FAFAFB] p-6"
              >
                <blockquote className="text-[14.5px] leading-relaxed text-[#0A1628]">
                  "{m.outcomeQuote}"
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <img
                    src={m.photoUrl}
                    alt={`${m.firstName}, ${m.roleOrCompany}`}
                    className="w-10 h-10 rounded-full object-cover border border-[#eef1f7]"
                  />
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-semibold text-[#0A1628]">
                      {m.firstName}
                    </p>
                    <p className="text-[12.5px] text-[#0A1628]/60 truncate">
                      {m.roleOrCompany}
                    </p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        ) : (
          // Empty state — intentionally shows the invitation, not fake
          // avatars. Replace the `FOUNDING_MEMBERS` array when the first
          // 5 quotes have been collected.
          <div
            className="mt-9 rounded-2xl border border-dashed border-[#dde2ec] bg-[#FAFAFB] p-8 text-center"
            data-testid="founding-members-empty"
          >
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#202B60]/10 text-[#202B60]">
              <Users size={22} />
            </span>
            <p className="mt-4 text-[15px] font-semibold text-[#0A1628]">
              Be one of the first names here.
            </p>
            <p className="mt-2 text-[13.5px] text-[#0A1628]/60 max-w-md mx-auto">
              Founding members get listed with name, role and the specific
              outcome Zynthoro delivered. Start the 7-day fictional
              demo — if it fits, you're in.
            </p>
            <Link
              to="/signup?trial=1"
              data-testid="founding-members-cta"
              className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#D4AF37] px-5 py-2.5 text-[14px] font-semibold text-[#0A1628] transition-opacity hover:opacity-90"
            >
              Start the 7-day demo
              <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
